/**
 * Minimal validation helpers for demographics (migrated from admin4.0 CommonValidation).
 */
import { CurrencySymbol } from "./Constants";

const ALPHA_VALIDATION = /[^A-Z a-z]+/;
const DECIMAL_NUMBER = /^(\d{0,6})((\.(\d{0,2})?)?)$/;
const EMAIL_VALIDATION = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
const GST_NO_VALIDATION = /^[a-zA-Z0-9]+$/;
/** Allow alphanumeric and _@./&+- for password input (same as admin4.0). */
const PASSWORD_INPUT = /^[A-Za-z0-9_@./&+-]*$/;

export default class CommonValidation {
  /** Returns true if value contains only letters and spaces. */
  static alphabetOnly = (value) => !ALPHA_VALIDATION.test(value);

  /** Trim and collapse multiple spaces. */
  static removeSpace = (value) => {
    if (typeof value === "string") {
      const text = value == null ? "" : value;
      return text.replace(/\s+/g, " ").trim();
    }
    return value;
  };

  /** Decimal validation (e.g. for concession/discount). */
  static DecimalNumber = (value) => DECIMAL_NUMBER.test(value);

  /** Format number to fixed decimal (n=2). Returns number. */
  static formatAmount = (value, n = 2) => {
    if (value === undefined || value === null || value === "") return 0;
    return +(+value).toFixed(n);
  };

  /**
   * Indian number formatting with currency symbol (e.g. ₹ 12,34,567.89).
   * showSymbol=false returns the formatted number without the leading symbol.
   * Returns "" for null/undefined/non-numeric input.
   */
  static AmountFormat = (InputAmount, showSymbol = true) => {
    if (InputAmount === undefined || InputAmount === null || InputAmount === "") return "";
    const formatpattern = /\B(?=(\d{2})+(?!\d))/g;
    let isNegativeAmount = false;
    let amount = InputAmount;

    if (typeof amount === "string") {
      isNegativeAmount = amount.includes("-");
      if (isNegativeAmount) amount = amount.replace("-", "");
      if (isNaN(+amount)) return "";
    } else if (typeof amount === "number") {
      if (isNaN(amount)) return "";
      isNegativeAmount = amount < 0;
      if (isNegativeAmount) amount = amount * -1;
    } else {
      return "";
    }

    const buildIndian = (intPart, decPart) => {
      const lastThree = intPart.substring(intPart.length - 3);
      let other = intPart.substring(0, intPart.length - 3);
      const formattedLast = other !== "" ? "," + lastThree : lastThree;
      const formattedOther = other.replace(formatpattern, ",");
      return `${showSymbol ? CurrencySymbol + " " : ""}${isNegativeAmount ? "- " : ""}${formattedOther}${formattedLast}.${decPart}`;
    };

    const str = String(amount);
    if (str.includes(".")) {
      const intPart = str.split(".")[0];
      let dec = str.split(".")[1] || "00";
      dec = dec.length === 1 ? dec + "0" : dec.slice(0, 2);
      return buildIndian(intPart, dec);
    }
    return buildIndian(str, "00");
  };

  /** Discount amount from percentage and total (rounded to 2 decimals). */
  static getDiscountAmountWithRound = (discountPercentage, totalAmount) => {
    if (discountPercentage === undefined || totalAmount === undefined || isNaN(discountPercentage) || isNaN(totalAmount)) return "";
    return +(+((+discountPercentage / 100) * +totalAmount).toFixed(2));
  };

  /** Discount percentage from amount and total. */
  static getDiscountPercentage = (discountAmount, totalAmount) => {
    if (discountAmount === undefined || totalAmount === undefined || isNaN(discountAmount) || isNaN(totalAmount)) return "";
    return (+discountAmount / +totalAmount) * 100;
  };

  /** Discount percentage rounded to 2 decimals. */
  static getDiscountPercentageWithRound = (discountAmount, totalAmount) => {
    if (discountAmount === undefined || totalAmount === undefined || isNaN(discountAmount) || isNaN(totalAmount)) return "";
    return +(+((+discountAmount / +totalAmount) * 100).toFixed(2));
  };

  /** Total amount including GST. */
  static calculateTotalWithGST = (amount, gstRate) => {
    if (amount === undefined || gstRate === undefined || isNaN(amount) || isNaN(gstRate)) return "";
    return +amount + (+amount * +gstRate) / 100;
  };

  /** Returns true if value contains only digits. */
  static numberOnly = (value) => {
    if (value === "" || value == null) return true;
    return /^\d+$/.test(String(value));
  };

  /** Returns true if value is a valid 10-digit mobile number. */
  static mobileNumber = (value) => {
    if (value === "" || value == null) return true;
    return /^\d{0,10}$/.test(String(value));
  };

  /** Returns true if value is a valid email format. */
  static emailValidation = (value) => {
    if (value === "" || value == null) return false;
    return EMAIL_VALIDATION.test(String(value));
  };

  /** Returns true if value is allowed for password input (alphanumeric and _@./&+-). */
  static PasswordInput = (value) => {
    if (value === "" || value == null) return true;
    return PASSWORD_INPUT.test(String(value));
  };

  /** Returns true if value contains only alphanumeric (for GSTIN typing). */
  static gstnumberValidation = (value) => {
    if (value === "" || value == null) return true;
    return GST_NO_VALIDATION.test(String(value));
  };

  /**
   * Returns true if transaction/reference number is mandatory for payment (from custom config).
   * Reads from sessionStorage key is_transaction_number_mandatory (set by Signin module load / CustomConfiguration).
   */
  static isTransactionNumberMandatory = () => {
    try {
      const v = typeof sessionStorage !== "undefined" ? sessionStorage.getItem("is_transaction_number_mandatory") : null;
      if (v === undefined || v === null) return false;
      return JSON.parse(v) === true;
    } catch {
      return false;
    }
  };
}
